import { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { FaSearch, FaFileExcel } from 'react-icons/fa'
import { FiRefreshCw } from 'react-icons/fi'
import api from '../../../libs/axios'
import { useDebounce } from '../../../hooks/useDebounce'
import toast from 'react-hot-toast'
import { Pagination } from '../../../utils/pagination.utils'
import { ExcelDownload } from '../../../utils/excel.utils'

interface User { id: number; first_name: string; last_name: string; username?: string; vendor_id?: number | null; vendor?: VendorInfo | null }
interface Activity { id: number; activity_name: string }
interface Project { id: number; project_name: string; project_short_name?: string }
interface VendorOwner { id: number; first_name: string; last_name: string; employee_code?: string }

interface Site {
  id: number
  site_id?: string
  site_name?: string
  activity?: Activity
  project?: Project
  site_engineer?: User
  report_designer?: User
  mis?: User
  team_coordinator?: User
  site_engineer_assign_date?: string
  site_engineer_work_date?: string
  ta_work_date?: string
  report_designer_work_date?: string
  validator_work_date?: string
  report_complete_date?: string | null
  report_sent_to_client_date?: string | null
  survey_workflow_status: string
}

interface VendorInfo {
  id: number
  vendor_full_name: string
  vendor_code?: string
  vendor_owner_user_id?: number
  owner?: VendorOwner
}

// Complete sites span both the Field Work and Report Preparation lifecycles, so the
// stage label is resolved against whichever lifecycle the status belongs to.
const FW_SURVEY_PENDING = ['site_created', 'engineer_assigned']
const FW_FIELD_DATA_UPLOAD_PENDING = ['field_work_in_progress', 'data_uploaded']
const RP_REPORT_PREP_PENDING = ['report_maker_assigned', 'report_in_progress', 'report_maker_rejected']
const RP_REPORT_COMPLETED = [
  'report_completed', 'validator_assigned',
  'validation_in_progress', 'validation_completed', 'validation_rejected', 'report_finalized',
]

function getStageLabel(status: string): string {
  if (status === 'report_sent_to_client') return 'Report Sent'
  if (RP_REPORT_COMPLETED.includes(status)) return 'Report Completed'
  if (RP_REPORT_PREP_PENDING.includes(status)) return 'Report Preparation Pending'
  if (FW_FIELD_DATA_UPLOAD_PENDING.includes(status)) return 'Field Data Upload Pending'
  if (FW_SURVEY_PENDING.includes(status)) return 'Survey Pending'
  return 'Survey Done'
}

function getStageColor(status: string): string {
  if (status === 'report_sent_to_client') return 'bg-green-100 text-green-700'
  if (RP_REPORT_COMPLETED.includes(status)) return 'bg-teal-100 text-teal-700'
  if (RP_REPORT_PREP_PENDING.includes(status)) return 'bg-orange-100 text-orange-700'
  if (FW_FIELD_DATA_UPLOAD_PENDING.includes(status)) return 'bg-yellow-100 text-yellow-700'
  if (FW_SURVEY_PENDING.includes(status)) return 'bg-blue-100 text-blue-700'
  return 'bg-green-100 text-green-700'
}

const GROUP_FILTERS = [
  { value: '', label: 'All Groups' },
  { value: 'survey_pending', label: 'Survey Pending' },
  { value: 'field_data_upload_pending', label: 'Field Data Upload Pending' },
  { value: 'survey_done', label: 'Survey Done' },
  { value: 'report_preparation_pending', label: 'Report Preparation Pending' },
  { value: 'report_completed', label: 'Report Completed' },
  { value: 'report_sent', label: 'Report Sent' },
]

const fmt = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fullName = (u?: { first_name: string; last_name: string } | null) => u ? `${u.first_name} ${u.last_name}` : '—'

// "Field Work Approved" = field work is done and TA-approved (ta_review_completed
// and everything downstream). Must match FIELD_WORK_APPROVED_STATUSES in
// server/src/controllers/v1/vendor_invoice.controller.ts.
const FIELD_WORK_APPROVED_STATUSES = [
  'ta_review_completed', 'mis_report_assign_pending', 'report_maker_assigned',
  'report_in_progress', 'report_completed', 'report_maker_rejected',
  'validator_assigned', 'validation_in_progress', 'validation_completed',
  'validation_rejected', 'report_finalized', 'report_sent_to_client',
]
const isFieldWorkApproved = (status: string) => FIELD_WORK_APPROVED_STATUSES.includes(status)

const HEADERS = [
  'Site ID', 'Site Name', 'Activity', 'Project', 'Engineer Name', 'Report Maker', 'MIS', 'Team Coordinator',
  'Vendor Code', 'Vendor Owner', 'Vendor Owner ID',
  'SE Assign Date', 'Field Work Start', 'FW Complete Date',
  'Report Assign Date', 'Report Completion Date', 'Report Validation Date', 'Report Sent to Client Date',
  'Field Work Approved', 'Stage',
]

export default function VendorPortalCompleteSites() {
  const [sites, setSites] = useState<Site[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [groupFilter, setGroupFilter] = useState('')
  const [vendor, setVendor] = useState<VendorInfo | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [exporting, setExporting] = useState(false)

  const debouncedSearch = useDebounce(searchInput, 400)

  const fetchSites = useCallback(() => {
    setLoading(true)
    const params: any = { page, limit }
    if (groupFilter) params.status_group = groupFilter
    if (debouncedSearch) params.search = debouncedSearch
    api.get('/vendor-portal/sites/complete', { params })
      .then(r => {
        setSites(r.data.data?.rows ?? [])
        setTotal(r.data.data?.count ?? 0)
        setVendor(r.data.data?.vendor ?? null)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [page, limit, groupFilter, debouncedSearch])

  useEffect(() => { fetchSites() }, [fetchSites])
  useEffect(() => { setPage(1) }, [debouncedSearch, groupFilter])

  // With one vendor in scope the API sends a single header vendor. Super Admin and
  // Vendor Manager browse every vendor at once, so `vendor` is null there and each row's
  // vendor is read off the assignee instead.
  const rowVendor = (s: Site): VendorInfo | null =>
    vendor ?? s.site_engineer?.vendor ?? s.report_designer?.vendor ?? null
  const vendorOwnerName = (s: Site) => {
    const v = rowVendor(s)
    return v?.owner ? fullName(v.owner) : (v?.vendor_full_name ?? '—')
  }
  const vendorOwnerId = (s: Site) => rowVendor(s)?.owner?.id ?? rowVendor(s)?.vendor_owner_user_id ?? '—'
  const vendorCode = (s: Site) => rowVendor(s)?.vendor_code ?? '—'

  const handleExport = async () => {
    setExporting(true)
    try {
      let all: Site[] = []
      let p = 1
      while (true) {
        const params: any = { page: p, limit: 100 }
        if (groupFilter) params.status_group = groupFilter
        if (debouncedSearch) params.search = debouncedSearch
        const r = await api.get('/vendor-portal/sites/complete', { params })
        const rows = r.data.data?.rows ?? []
        all = [...all, ...rows]
        if (rows.length < 100) break
        p++
      }
      setExportData(all.map(s => ({
        'Site ID': s.site_id ?? '—',
        'Site Name': s.site_name ?? '—',
        'Activity': s.activity?.activity_name ?? '—',
        'Project': s.project?.project_short_name ?? s.project?.project_name ?? '—',
        'Engineer Name': fullName(s.site_engineer),
        'Report Maker': fullName(s.report_designer),
        'MIS': fullName(s.mis),
        'Team Coordinator': fullName(s.team_coordinator),
        'Vendor Code': vendorCode(s),
        'Vendor Owner': vendorOwnerName(s),
        'Vendor Owner ID': String(vendorOwnerId(s)),
        'SE Assign Date': fmt(s.site_engineer_assign_date),
        'Field Work Start': fmt(s.site_engineer_work_date),
        'FW Complete Date': fmt(s.ta_work_date),
        'Report Assign Date': fmt(s.report_designer_work_date),
        'Report Completion Date': fmt(s.report_complete_date),
        'Report Validation Date': fmt(s.validator_work_date),
        'Report Sent to Client Date': fmt(s.report_sent_to_client_date),
        'Field Work Approved': isFieldWorkApproved(s.survey_workflow_status) ? 'Approved' : 'Not Approved',
        'Stage': getStageLabel(s.survey_workflow_status),
      })))
      setExportOpen(true)
    } catch {
      toast.error('Failed to export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <Helmet><title>Vendor Portal – Complete Sites</title></Helmet>
      <div className="space-y-4">

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Complete Sites</h2>
              <p className="text-sm text-gray-500 mt-0.5">{total} sites where your team is both the site engineer and the report maker</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search site name / ID / ref…"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded text-sm w-full sm:w-60 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4 pointer-events-none" />
                {searchInput && (
                  <button onClick={() => setSearchInput('')} className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600 text-xs">✕</button>
                )}
              </div>
              <select
                value={groupFilter}
                onChange={e => { setGroupFilter(e.target.value); setPage(1) }}
                className="px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500"
              >
                {GROUP_FILTERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
              <button
                onClick={fetchSites}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm"
              >
                <FiRefreshCw className="w-4 h-4" /> Refresh
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50"
              >
                <FaFileExcel className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {HEADERS.map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(HEADERS.length)].map((_, j) => (
                        <td key={j} className="px-3 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                      ))}
                    </tr>
                  ))
                ) : sites.length === 0 ? (
                  <tr>
                    <td colSpan={HEADERS.length} className="px-4 py-10 text-center text-gray-400 text-sm">
                      No complete sites found
                    </td>
                  </tr>
                ) : sites.map(site => (
                  <tr key={site.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-3 text-gray-500 text-xs font-mono">{site.site_id ?? '—'}</td>
                    <td className="px-3 py-3 font-medium text-gray-900">{site.site_name ?? '—'}</td>
                    <td className="px-3 py-3 text-gray-600">{site.activity?.activity_name ?? '—'}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{site.project?.project_short_name ?? site.project?.project_name ?? '—'}</td>
                    <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(site.site_engineer)}</td>
                    <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(site.report_designer)}</td>
                    <td className="px-3 py-3 text-gray-700 text-xs whitespace-nowrap">{fullName(site.mis)}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fullName(site.team_coordinator)}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs font-mono">{vendorCode(site)}</td>
                    <td className="px-3 py-3 text-gray-700">{vendorOwnerName(site)}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{String(vendorOwnerId(site))}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(site.site_engineer_assign_date)}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(site.site_engineer_work_date)}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(site.ta_work_date)}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(site.report_designer_work_date)}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(site.report_complete_date)}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(site.validator_work_date)}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{fmt(site.report_sent_to_client_date)}</td>
                    <td className="px-3 py-3">
                      {isFieldWorkApproved(site.survey_workflow_status) ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">Approved</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 whitespace-nowrap">Not Approved</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStageColor(site.survey_workflow_status)}`}>
                        {getStageLabel(site.survey_workflow_status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            pagination={{
              page,
              limit,
              total_count: total,
              total_pages: Math.ceil(total / limit) || 1,
              has_next_page: page * limit < total,
              has_prev_page: page > 1,
              next_page: page * limit < total ? page + 1 : null,
              prev_page: page > 1 ? page - 1 : null,
              offset: (page - 1) * limit,
            }}
            onPageChange={p => setPage(p)}
            onLimitChange={l => { setLimit(l); setPage(1) }}
          />
        </div>
      </div>

      <ExcelDownload
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        data={exportData}
        filename="complete_sites"
        title="Complete Sites Export"
        description="Sites where your team is both the site engineer and the report maker"
      />
    </>
  )
}
